<template>
  <div class="popup z-index-10-fg confirmation small-popup link-contact-popup" ref="popupWindow">
    <div class="popup-content" ref="popupContent">
      <div class="popup-header">
        <h2>{{ titleText }}</h2>
        <img src="../assets/close.svg" alt="" class="popup-close" @click="$emit('closePopup')" />
      </div>
      <div v-if="!showEditContact" class="popup-body">
        <div>
          <h3>Create New Contact</h3>
          <div class="link-button" @click="editContact(true)">
            <span>{{ selectedLead.NAME }}</span>
            <span v-if="leadAddress.LINE1">{{ leadAddress.LINE1 }}</span>
            <span v-if="leadAddress.LINE2">{{ leadAddress.LINE2 }}</span>
            <span v-if="leadAddress.CITY || leadAddress.STATE || leadAddress.POSTALCODE">{{ leadAddress.CITY ? leadAddress.CITY + ', ' : '' }}{{ leadAddress.STATE }} {{ leadAddress.POSTALCODE }}</span>
            <span v-for="phone in selectedLead.phones">{{ formatPhone(phone.NUMBERDISPLAY) }}</span>
            <span v-if="selectedLead.email">{{ selectedLead.email?.ADDRESS }}</span>
          </div>
        </div>
        <div>
          <h3>Link to Existing Record</h3>
          <img v-if="loading" src="../assets/spinner.svg" alt="" />
          <p v-else-if="!loading && error">Error finding similar records.</p>
          <p v-else-if="!loading && !error && !duplicateCompanies.length && !duplicateContacts.length">No similar records found.</p>

          <div class="link-button" v-for="company in duplicateCompanies" @click="linkContact(company.COMPANYID, 'company')">
            <span :class="{ bold: selectedLead.NAME == company.NAME }">{{ company.NAME }}</span>
            <span v-if="getAddress(company).LINE1" :class="{ bold: leadAddress.LINE1 == getAddress(company).LINE1 }">{{ getAddress(company).LINE1 }}</span>
            <span v-if="getAddress(company).LINE2" :class="{ bold: leadAddress.LINE2 == getAddress(company).LINE2 }">{{ getAddress(company).LINE2 }}</span>
            <span v-if="getAddress(company).CITY || getAddress(company).STATE || getAddress(company).POSTALCODE" :class="{ bold: getAddress(company).CITY == leadAddress.CITY }"
              >{{ getAddress(company).CITY ? getAddress(company).CITY + ', ' : '' }}{{ getAddress(company).STATE }} {{ getAddress(company).POSTALCODE }}</span
            >
            <span v-for="phone in company.phones" :class="{ bold: phoneExistInLead(phone.NUMBERDISPLAY) }">{{ formatPhone(phone.NUMBERDISPLAY) }}</span>
            <div class="edit" @click.stop="editContact(false, company.COMPANYID, 'company')">
              <img id="noHover" src="../assets/pencil.svg" />
              <img id="hover" src="../assets/pencil-hover.svg" />
            </div>
            <div v-if="isSaving == company.COMPANYID" class="saving">
              <img src="../assets/spinner.svg" />
            </div>
          </div>

          <div class="link-button" v-for="contact in duplicateContacts" @click="linkContact(contact.CONTACTID, 'individual')">
            <span :class="{ bold: selectedLead.NAME == `${contact.FIRSTNAME} ${contact.LASTNAME}` }">{{ contact.FIRSTNAME }} {{ contact.LASTNAME }}</span>
            <span v-if="contact.company?.NAME" :class="{ bold: selectedLead.NAME == contact.company?.NAME }">({{ contact.company?.NAME }})</span>
            <span v-if="getAddress(contact).LINE1" :class="{ bold: leadAddress.LINE1 == getAddress(contact).LINE1 }">{{ getAddress(contact).LINE1 }}</span>
            <span v-if="getAddress(contact).LINE2" :class="{ bold: leadAddress.LINE2 == getAddress(contact).LINE2 }">{{ getAddress(contact).LINE2 }}</span>
            <span v-if="getAddress(contact).CITY || getAddress(contact).STATE || getAddress(contact).POSTALCODE" :class="{ bold: getAddress(contact).CITY == leadAddress.CITY }"
              >{{ getAddress(contact).CITY ? getAddress(contact).CITY + ', ' : '' }}{{ getAddress(contact).STATE }} {{ getAddress(contact).POSTALCODE }}</span
            >
            <span v-for="phone in contact.phones" :class="{ bold: phoneExistInLead(phone.NUMBERDISPLAY) }">{{ formatPhone(phone.NUMBERDISPLAY) }}</span>
            <span v-if="contact.email" :class="{ bold: selectedLead.email.ADDRESS == contact.email.ADDRESS }">{{ contact.email.ADDRESS }}</span>
            <div class="edit" @click.stop="editContact(false, contact.CONTACTID, 'individual')">
              <img id="noHover" src="../assets/pencil.svg" />
              <img id="hover" src="../assets/pencil-hover.svg" />
            </div>
            <div v-if="isSaving == contact.CONTACTID" class="saving">
              <img src="../assets/spinner.svg" />
            </div>
          </div>
        </div>
      </div>
      <EditContact v-if="showEditContact" :isNew="contactIsNew" :contactInfo="contactToEdit" :type="contactEditType" :leadid="leadid" @back="showEditContact = false" @closePopup="$emit('closePopup')" />
    </div>
  </div>
  <div class="screen-overlay-gray z-index-10-bg"></div>
</template>

<script>
import useLeadsQuery from '@/composables/useLeadsQuery';
import useFindDuplicates from '@/composables/useFindDuplicates';
import { computed } from 'vue';
import EditContact from './EditContact.vue';
import useLeadMutate from '@/composables/useLeadMutate';
import useAlerts from '@/composables/useAlerts';

export default {
  name: 'AddLead',
  emits: ['closePopup'],
  props: ['leadid'],
  components: {
    EditContact,
  },
  setup(props) {
    const selectedLead = computed(() => {
      return useLeadsQuery.leads.find((lead) => lead.LEADID === props.leadid) || null;
    });

    const { result, loading, error } = useFindDuplicates(props.leadid);
    const { updateLead } = useLeadMutate();
    const { addAlert } = useAlerts();

    return {
      leads: useLeadsQuery.leadsIncludingSingleLead,
      result,
      loading,
      error,
      selectedLead,
      addAlert,
      updateLead,
    };
  },
  data() {
    return {
      contactIsNew: true,
      contactToEdit: {},
      showEditContact: false,
      contactEditType: null,
      isSaving: null,
    };
  },
  computed: {
    leadAddress() {
      return this.selectedLead.address || {};
    },
    duplicateCompanies() {
      return this.result?.findDuplicates?.companies || [];
    },
    duplicateContacts() {
      return this.result?.findDuplicates?.contacts || [];
    },
    titleText() {
      if (!this.showEditContact) return 'Create or Link an Existing Contact';
      if (this.contactIsNew) return 'Create Contact and Link to Lead';
      else return 'Edit Existing CMS Contact';
    },
  },
  methods: {
    formatPhone(num) {
      const numbers = num?.replace(/\D/g, '') || '';
      const char = { 0: '(', 3: ') ', 6: '-' };
      let newValue = '';
      for (let i = 0; i < numbers.length; i++) {
        newValue += (char[i] || '') + numbers[i];
      }
      return newValue;
    },
    getAddress(contact) {
      return contact.address || {};
    },
    phoneExistInLead(phone) {
      return this.selectedLead.phones.some((leadPhone) => this.formatPhone(leadPhone.NUMBERDISPLAY) === this.formatPhone(phone));
    },
    editContact(newContact, id, type) {
      if (this.isSaving) return;

      if (newContact) {
        this.contactIsNew = true;
        if (this.selectedLead.email) this.contactEditType = 'individual';
        else this.contactEditType = 'company';

        this.contactToEdit = {
          NAME: this.selectedLead.NAME,
          FIRSTNAME: (this.selectedLead.NAME.split(' ')[0] || '').charAt(0).toUpperCase() + (this.selectedLead.NAME.split(' ')[0] || '').slice(1),
          LASTNAME: (this.selectedLead.NAME.split(' ')[1] || '').charAt(0).toUpperCase() + (this.selectedLead.NAME.split(' ')[1] || '').slice(1),
          address: { ...(this.leadAddress || {}) },
          phone: { ...(this.selectedLead.phones[0] || {}) },
          email: { ...(this.selectedLead.email || {}) },
        };
      } else {
        this.contactIsNew = false;
        this.contactEditType = type;
        const selectedContact = this.duplicateCompanies.find((company) => company.COMPANYID === id) || this.duplicateContacts.find((contact) => contact.CONTACTID === id) || {};
        this.contactToEdit = {
          COMPANYID: selectedContact.COMPANYID || null,
          CONTACTID: selectedContact.CONTACTID || null,
          NAME: selectedContact.NAME || '',
          FIRSTNAME: selectedContact.FIRSTNAME || '',
          LASTNAME: selectedContact.LASTNAME || '',
          address: { ...(this.getAddress(selectedContact) || {}) },
          phone: { ...(selectedContact.phones[0] || {}) },
          email: { ...(selectedContact.email || {}) },
        };
      }

      this.showEditContact = true;
    },
    async linkContact(id, type) {
      if (this.isSaving) return;
      this.isSaving = id;
      const leadToSave = {};

      if (type === 'individual') {
        const dupContact = this.duplicateContacts.find((contact) => contact.CONTACTID === id);
        this.selectedLead.CONTACTID = id;
        this.selectedLead.contact = {};
        this.selectedLead.contact.FIRSTNAME = dupContact.FIRSTNAME;
        this.selectedLead.contact.LASTNAME = dupContact.LASTNAME;
        leadToSave.CONTACTID = id;
        leadToSave.COMPANYID = null;
      } else if (type === 'company') {
        const dupCompany = this.duplicateCompanies.find((company) => company.COMPANYID === id);
        this.selectedLead.COMPANYID = id;
        this.selectedLead.company = {};
        this.selectedLead.company.NAME = dupCompany.NAME;
        leadToSave.CONTACTID = null;
        leadToSave.COMPANYID = id;
      }

      leadToSave.LINKMETHOD = 'Linked';
      this.selectedLead.LINKMETHOD = 'Linked';
      await this.updateLead(this.leadid, leadToSave);

      this.addAlert('Contact linked successfully.', 'low', 3);
      this.$emit('closePopup');
    },
    calculateWindowDimensions() {
      this.$nextTick(() => {
        const newHeight = this.$refs.popupContent.scrollHeight + 'px';
        this.$refs.popupWindow.style.height = newHeight;
      });
    },
  },
  mounted() {
    this.calculateWindowDimensions();
  },
  updated() {
    this.$refs.popupWindow.style.transition = 'height 0.5s ease';
    this.calculateWindowDimensions();
  },
};
</script>

<style lang="scss" scoped>
.link-contact-popup.popup.small-popup {
  width: 45rem;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  h2 {
    text-align: center;
    font-size: $fontSize28;
    width: 100%;
    font-weight: 400;
  }

  .popup-body {
    display: flex;
    flex-direction: row;
    margin-top: 1rem;
    flex-wrap: nowrap;
    flex-direction: space-around;
    gap: 1.5rem 4rem;
    padding: 2rem;
    margin-bottom: 0;

    & > div {
      flex: 1;

      h3 {
        font-size: $fontSize16;
        font-weight: 500;
        margin-bottom: 1rem;
      }

      .link-button {
        display: flex;
        flex-direction: column;
        padding: 0.75rem;
        border: 1px solid $inputBorderColor;
        border-radius: $borderRadius4;
        background: linear-gradient(0deg, #fcfcfc, #f9f9f9);
        cursor: pointer;
        margin-bottom: 1rem;
        position: relative;

        span {
          font-size: $fontSize14;
          color: black;
          font-weight: 400;

          &.bold {
            font-weight: 600;
          }
        }

        &:hover {
          border-color: $primaryActionColor;
          background: linear-gradient(180deg, #fcfcfc, #f9f9f9);
        }

        .edit {
          position: absolute;
          right: 0.5rem;
          top: 0.4rem;

          #hover {
            display: none;
          }

          &:hover {
            #noHover {
              display: none;
            }

            #hover {
              display: block;
            }
          }
        }

        .saving {
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: $borderRadius5;
          background: rgba(255, 255, 255, 0.8);
        }
      }

      img {
        width: 2rem;
        height: auto;
      }

      p {
        font-size: $fontSize14;
      }
    }
  }
}
</style>
